/**
 * Threshold selector form composed from smaller pieces.
 */

import styled from '@emotion/styled';
import SearchIcon from '@mui/icons-material/Search';
import {
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import type { UIThresholdModel } from './catalog-transform';
import { ThresholdSelectorCard } from './threshold-selector-card';

// -- type definitions --

export interface ThresholdSelectorFormProps {
  readonly thresholds?: readonly UIThresholdModel[];
  readonly selectedThresholdIds?: readonly number[];
  readonly defaultSelectedThresholdIds?: readonly number[];
  readonly onSelectionChange?: (selectedIds: number[]) => void;
  readonly onEditRequest?: (threshold: UIThresholdModel) => void;
  readonly selectionIdMode?: 'assignment' | 'threshold';
  readonly deduplicateBySelectionId?: boolean;
  readonly title?: string;
  readonly helperText?: string;
  readonly emptyMessage?: string;
  readonly disabled?: boolean;
}

// -- constants --

const DEFAULT_TITLE = 'Sélecteur de seuils';
const DEFAULT_HELPER_TEXT =
  'Activez les seuils à représenter et à évaluer dans le graphique.';
const DEFAULT_EMPTY_MESSAGE =
  'Aucun seuil disponible pour la configuration actuelle.';

// -- styled components --

const FormLayout = styled.section`
  display: grid;
  gap: 16px;
  min-width: 0;
`;

// -- functions --

function orderSelectedIds(
  thresholds: readonly UIThresholdModel[],
  selectedIds: ReadonlySet<number>
): number[] {
  return thresholds
    .map((threshold) => threshold.id)
    .filter((thresholdId) => selectedIds.has(thresholdId));
}

function getSelectionId(
  threshold: UIThresholdModel,
  selectionIdMode: 'assignment' | 'threshold'
): number {
  return selectionIdMode === 'threshold' ? threshold.thresholdId : threshold.id;
}

function deduplicateThresholdsBySelectionId(
  thresholds: readonly UIThresholdModel[],
  selectionIdMode: 'assignment' | 'threshold'
): UIThresholdModel[] {
  const groupedBySelectionId = new Map<number, UIThresholdModel>();

  thresholds.forEach((threshold) => {
    const selectionId = getSelectionId(threshold, selectionIdMode);
    const existing = groupedBySelectionId.get(selectionId);

    if (!existing) {
      groupedBySelectionId.set(selectionId, {
        ...threshold,
        id: selectionId,
      });
      return;
    }

    const mergedBindingKeySet = new Set(
      existing.sourceBindings.map(
        (binding) => `${binding.measure_point_id}-${binding.measure_type_id}`
      )
    );
    const mergedBindings = [...existing.sourceBindings];

    threshold.sourceBindings.forEach((binding) => {
      const bindingKey = `${binding.measure_point_id}-${binding.measure_type_id}`;

      if (!mergedBindingKeySet.has(bindingKey)) {
        mergedBindingKeySet.add(bindingKey);
        mergedBindings.push({ ...binding });
      }
    });

    groupedBySelectionId.set(selectionId, {
      ...existing,
      sourceBindings: mergedBindings,
    });
  });

  return [...groupedBySelectionId.values()];
}

function matchesSearchQuery(
  threshold: UIThresholdModel,
  searchQuery: string
): boolean {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  if (normalizedQuery.length === 0) {
    return true;
  }

  return (
    threshold.label.toLowerCase().includes(normalizedQuery) ||
    threshold.variables.some((variable) =>
      variable.toLowerCase().includes(normalizedQuery)
    ) ||
    threshold.variableDetails.some((variable) =>
      variable.variableName.toLowerCase().includes(normalizedQuery)
    )
  );
}

/**
 * Main threshold selector form.
 */
export function ThresholdSelectorForm({
  thresholds = [],
  selectedThresholdIds,
  defaultSelectedThresholdIds = [],
  onSelectionChange,
  onEditRequest,
  selectionIdMode = 'assignment',
  deduplicateBySelectionId = false,
  title = DEFAULT_TITLE,
  helperText = DEFAULT_HELPER_TEXT,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  disabled = false,
}: ThresholdSelectorFormProps) {
  const [internalSelection, setInternalSelection] = useState<number[]>([
    ...defaultSelectedThresholdIds,
  ]);
  const [expandedDetails, setExpandedDetails] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const resolvedSelection = useMemo(
    () =>
      selectedThresholdIds !== undefined
        ? [...selectedThresholdIds]
        : internalSelection,
    [internalSelection, selectedThresholdIds]
  );

  const selectedSet = useMemo(
    () => new Set<number>(resolvedSelection),
    [resolvedSelection]
  );

  const expandedSet = useMemo(
    () => new Set<number>(expandedDetails),
    [expandedDetails]
  );

  const displayThresholds = useMemo(
    () =>
      deduplicateBySelectionId
        ? deduplicateThresholdsBySelectionId(thresholds, selectionIdMode)
        : thresholds.map((threshold) => ({
            ...threshold,
            id: getSelectionId(threshold, selectionIdMode),
          })),
    [deduplicateBySelectionId, selectionIdMode, thresholds]
  );

  const filteredThresholds = useMemo(
    () =>
      displayThresholds.filter((threshold) =>
        matchesSearchQuery(threshold, searchQuery)
      ),
    [displayThresholds, searchQuery]
  );

  const handleToggle = (thresholdId: number) => {
    if (disabled) {
      return;
    }

    const nextSelectionSet = new Set(selectedSet);

    if (nextSelectionSet.has(thresholdId)) {
      nextSelectionSet.delete(thresholdId);
    } else {
      nextSelectionSet.add(thresholdId);
    }

    const nextSelection = orderSelectedIds(displayThresholds, nextSelectionSet);

    if (selectedThresholdIds === undefined) {
      setInternalSelection(nextSelection);
    }

    onSelectionChange?.(nextSelection);
  };

  const handleToggleDetails = (thresholdId: number) => {
    setExpandedDetails((current) =>
      current.includes(thresholdId)
        ? current.filter((item) => item !== thresholdId)
        : [...current, thresholdId]
    );
  };

  return (
    <FormLayout>
      <input
        type="hidden"
        name="selected_thresholds"
        value={JSON.stringify(resolvedSelection)}
        readOnly
      />

      <Stack spacing={0.75}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${resolvedSelection.length} sélectionné${
              resolvedSelection.length > 1 ? 's' : ''
            }`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${displayThresholds.length} seuil${
              displayThresholds.length > 1 ? 's' : ''
            }`}
          />
        </Stack>
      </Stack>

      {displayThresholds.length > 0 ? (
        <TextField
          fullWidth
          size="small"
          label="Rechercher"
          placeholder="Rechercher par nom ou variable"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      ) : null}

      {displayThresholds.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </CardContent>
        </Card>
      ) : filteredThresholds.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Aucun seuil ne correspond à la recherche actuelle.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {filteredThresholds.map((threshold) => (
            <ThresholdSelectorCard
              key={threshold.id}
              threshold={threshold}
              selected={selectedSet.has(threshold.id)}
              detailsOpen={expandedSet.has(threshold.id)}
              onToggle={handleToggle}
              onToggleDetails={handleToggleDetails}
              onEditRequest={onEditRequest}
              disabled={disabled}
            />
          ))}
        </Stack>
      )}
    </FormLayout>
  );
}

export default ThresholdSelectorForm;
